import { Component } from "react";
import storage from "@/lib/storage";

// Last-resort safety net. Without this, an uncaught error anywhere in the tree
// (a bad API response shape, a chart choking on unexpected data, a native plugin
// callback, etc.) makes React unmount the ENTIRE app with zero trace — the user
// is left staring at the bare body background (beige), no logo, no message, no
// way back. This was the iOS "hangs a few seconds after opening" bug: the
// dashboard rendered fine, then something threw during a later re-render and
// the whole UI vanished silently.
// Deliberately avoids importing Logo/Button/etc — this is the fallback for when
// something in the tree is broken, so it must not share import surface with it.
export default class ErrorBoundary extends Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, message: "", stack: "" };
    }

    static getDerivedStateFromError(error) {
        return { hasError: true, message: (error && error.message) || String(error) };
    }

    componentDidCatch(error, info) {
        const stack = info && info.componentStack ? info.componentStack.trim().split("\n").slice(0, 3).join("\n") : "";
        this.setState({ stack });
        console.error("[ErrorBoundary]", error, info && info.componentStack);
    }

    handleRetry = async () => {
        // Si el fallo es un chunk que ya no existe en el servidor, recargar a secas
        // puede volver a servir los assets viejos que el service worker tiene
        // cacheados cache-first. Limpiamos CacheStorage antes (best effort).
        try {
            if (typeof caches !== "undefined") {
                const keys = await caches.keys();
                await Promise.all(keys.map((k) => caches.delete(k)));
            }
        } catch {
            // da igual: lo que arregla el problema es la recarga
        }
        window.location.reload();
    };

    handleReset = () => {
        storage.removeItem("qc_token");
        storage.removeItem("qc_portfolio");
        window.location.href = "/";
    };

    render() {
        if (!this.state.hasError) return this.props.children;
        // Un chunk que no carga no es un fallo de la app: es una pestana con un build
        // caducado. Al usuario no le sirve de nada el stack, solo el boton.
        const chunkFailed = /loading chunk|loading css chunk|dynamically imported module/i.test(
            this.state.message || "",
        );
        // Sin red el mensaje de webpack es el mismo, pero decirle al usuario que hay
        // una version nueva seria mentira.
        const offline = chunkFailed && typeof navigator !== "undefined" && navigator.onLine === false;
        const isStaleBuild = chunkFailed && !offline;
        return (
            <div className="fixed inset-0 z-[9999] bg-stone flex flex-col items-center justify-center gap-6 px-8 text-center">
                <span className="font-display text-2xl tracking-tight text-graphite">
                    <span className="font-medium">Q</span>
                    <span className="text-petrol">capital</span>
                </span>
                <div className="max-w-xs">
                    <p className="font-medium text-graphite mb-2">
                        {offline && "Parece que no hay conexion."}
                        {isStaleBuild && "Hay una version nueva de QCapital."}
                        {!chunkFailed && "No hemos podido mostrar la app."}
                    </p>
                    {chunkFailed ? (
                        <p className="text-xs text-soft_muted">
                            {offline ? "Comprueba tu conexion y reintenta." : "Actualiza para seguir usando la app."}
                        </p>
                    ) : (
                        <>
                            {this.state.message && (
                                <p className="text-xs text-soft_muted break-words max-h-24 overflow-auto">{this.state.message}</p>
                            )}
                            {this.state.stack && (
                                <p className="text-[10px] text-soft_muted/70 break-words max-h-24 overflow-auto mt-2 font-mono whitespace-pre-wrap">
                                    {this.state.stack}
                                </p>
                            )}
                        </>
                    )}
                </div>
                <div className="flex flex-col items-center gap-3 w-full max-w-[220px]">
                    <button
                        onClick={this.handleRetry}
                        className="w-full bg-petrol hover:bg-petrol-hover text-bone rounded-xl px-6 py-3 text-sm font-medium"
                    >
                        {isStaleBuild ? "Actualizar" : "Reintentar"}
                    </button>
                    <button onClick={this.handleReset} className="text-xs text-soft_muted underline">
                        Cerrar sesion y reintentar
                    </button>
                </div>
            </div>
        );
    }
}
